/**
 * Scanbrix iOS app product analytics events (Firebase Analytics → GA4 property 507368100).
 * Website uses a separate GA4 property — do not mix when backfilling.
 */

/** GA4 property id for the iOS app (not the marketing website). */
export const IOS_GA4_PROPERTY_ID = "507368100";

export const ONBOARDING_EVENTS = [
  "onboarding_started",
  "onboarding_step_completed",
  "onboarding_completed",
];

export const POST_ONBOARDING_EVENTS = [
  "post_onboarding_scan_prompt_shown",
  "post_onboarding_start_scanning_tapped",
  "post_onboarding_watch_tutorial_tapped",
];

export const AUTH_CLICK_EVENTS = ["sign_up_completed", "login_completed", "login", "sign_up"];

export const WORKSPACE_CLICK_EVENTS = ["practice_workspace_opened", "workspace_created"];

export const SCAN_CLICK_EVENTS = [
  "scan_started",
  "scan_stopped",
  "scan_saved",
  "scan_finished",
  "scan_completed",
];

export const UPLOAD_CLICK_EVENTS = ["upload_button_tapped", "file_upload_started"];

export const ORDER_CLICK_EVENTS = ["order_started", "order_placed", "purchase"];

export const SCAN_EVENTS = ["scan_started", "scan_finished", "scan_completed"];

/** Product events that indicate the user started scanning in auto Practice Space. */
export const PRACTICE_SPACE_SCAN_SIGNAL_EVENTS = [
  "practice_workspace_opened",
  "post_onboarding_start_scanning_tapped",
];

/** GA4 user property / custom dimension parameter — must match iOS Analytics.setUserProperty. */
export const IOS_GA4_USER_PROPERTY = "sb_uid";

/** All events backfilled from iOS GA4 into `product_analytics_events`. */
export const IOS_PRODUCT_ANALYTICS_EVENTS = [
  ...AUTH_CLICK_EVENTS,
  ...ONBOARDING_EVENTS,
  ...POST_ONBOARDING_EVENTS,
  ...WORKSPACE_CLICK_EVENTS,
  ...SCAN_CLICK_EVENTS,
  ...UPLOAD_CLICK_EVENTS,
  ...ORDER_CLICK_EVENTS,
];

const EVENT_LABELS = {
  sign_up_completed: "Sign-up completed",
  login_completed: "Login completed",
  login: "Login",
  sign_up: "Sign up",
  onboarding_started: "Onboarding started",
  onboarding_step_completed: "Onboarding step",
  onboarding_completed: "Onboarding completed",
  post_onboarding_scan_prompt_shown: "Scan prompt shown",
  post_onboarding_start_scanning_tapped: "Start Scanning tapped",
  post_onboarding_watch_tutorial_tapped: "Watch Tutorial tapped",
  practice_workspace_opened: "Practice workspace opened",
  workspace_created: "Workspace created",
  scan_started: "Started scanning",
  scan_stopped: "Stopped scanning",
  scan_saved: "Scan saved",
  scan_finished: "Finished scanning",
  scan_completed: "Scan completed",
  upload_button_tapped: "Upload button tapped",
  file_upload_started: "File upload started",
  order_started: "Order started",
  order_placed: "Order placed",
  purchase: "Purchase",
};

/** CRM timeline / activity type for a normalized event name. */
export function activityTypeForProductEvent(eventName) {
  const n = normalizeProductEventName(eventName);
  if (SCAN_EVENTS.includes(n) || n === "scan_finished") return n === "scan_finished" || n === "scan_completed" ? "scan_finished" : "scan_started";
  if (n.startsWith("onboarding")) return "onboarding_event";
  if (n.startsWith("post_onboarding")) return "activation_prompt_event";
  return "product_analytics_event";
}

/** Normalize app vs CRM naming (`scan_completed` → treat as scan finished). */
export function normalizeProductEventName(raw) {
  const n = String(raw ?? "").trim().toLowerCase();
  if (n === "scan_completed") return "scan_completed";
  return n;
}

export function isIosProductAnalyticsEvent(doc) {
  const n = normalizeProductEventName(doc?.event_name ?? doc?.eventName);
  return IOS_PRODUCT_ANALYTICS_EVENTS.includes(n);
}

export function productEventLabel(eventName) {
  const n = normalizeProductEventName(eventName);
  return EVENT_LABELS[n] || n.replace(/_/g, " ");
}

export function buildProductEventActivityText(doc) {
  const n = normalizeProductEventName(doc?.event_name ?? doc?.eventName);
  const ws = doc?.workspace_name ?? doc?.workspaceName;
  const suffix = ws ? ` · ${String(ws).trim()}` : "";
  if (n === "scan_started") return `Started scanning${suffix}`;
  if (n === "scan_finished" || n === "scan_completed") return `Scan completed${suffix}`;
  if (n === "sign_up_completed") return "Sign-up completed";
  if (n === "login_completed" || n === "login") return "Logged in";
  if (n === "practice_workspace_opened") return "Opened Practice Space workspace";
  if (n === "workspace_created") return `Created a workspace${suffix}`;
  if (n === "upload_button_tapped" || n === "file_upload_started") return "Tapped upload";
  if (n === "scan_stopped") return "Stopped scanning";
  if (n === "scan_saved") return "Saved scan";
  if (n === "order_started" || n === "order_placed" || n === "purchase") return "Order action";
  if (n === "post_onboarding_scan_prompt_shown") return "Post-onboarding scan prompt shown";
  if (n === "post_onboarding_start_scanning_tapped") return "Tapped Start Scanning on activation prompt";
  if (n === "post_onboarding_watch_tutorial_tapped") return "Tapped Watch Tutorial on activation prompt";
  if (n === "onboarding_completed") return "Onboarding questions completed";
  if (n === "onboarding_started") return "Onboarding questions started";
  if (n === "onboarding_step_completed") {
    const step = doc?.step ?? doc?.event_params?.step;
    return step != null ? `Onboarding step ${step} completed` : "Onboarding step completed";
  }
  return productEventLabel(n);
}

function eventTimestampMs(ev) {
  const raw = ev?.created_at ?? ev?.createdAt ?? ev?.timestamp;
  if (raw == null) return 0;
  if (typeof raw.toMillis === "function") return raw.toMillis();
  if (typeof raw === "object" && typeof raw.seconds === "number") return raw.seconds * 1000;
  if (typeof raw === "number" && raw > 1e11) return raw;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : 0;
}

/** First event timestamp per user ref for each event name. */
export function indexFirstEventMsByUser(productAnalyticsEvents = [], eventNames = []) {
  const names = new Set(eventNames.map(normalizeProductEventName));
  const byUser = new Map();
  for (const ev of productAnalyticsEvents) {
    const name = normalizeProductEventName(ev?.event_name ?? ev?.eventName);
    if (!names.has(name)) continue;
    const uid = String(ev?.uid ?? ev?.user_id ?? ev?.userId ?? "").trim();
    if (!uid) continue;
    const ms = eventTimestampMs(ev);
    if (ms <= 0) continue;
    const key = `${uid}::${name}`;
    const prev = byUser.get(key);
    if (prev == null || ms < prev) byUser.set(key, ms);
  }
  return byUser;
}

/** User refs that have at least one matching event. */
export function collectEventUserRefs(productAnalyticsEvents = [], eventNames = []) {
  const names = new Set(eventNames.map(normalizeProductEventName));
  const refs = new Set();
  for (const ev of productAnalyticsEvents) {
    const name = normalizeProductEventName(ev?.event_name ?? ev?.eventName);
    if (!names.has(name)) continue;
    const uid = String(ev?.uid ?? ev?.user_id ?? ev?.userId ?? "").trim();
    if (uid) refs.add(uid);
  }
  return refs;
}

export function hasProductAnalyticsPromptEvents(productAnalyticsEvents = []) {
  return productAnalyticsEvents.some((ev) => {
    const n = normalizeProductEventName(ev?.event_name ?? ev?.eventName);
    return POST_ONBOARDING_EVENTS.includes(n);
  });
}
